import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { AppDataService, RiderService, XpressUserService } from 'app/services';
import { MenuPermissionActionEnum, MenuPermissionConfig, XpressAppTypeEnum } from 'app/models';
import * as moment from 'moment';

interface SendLogRequestStatus {
    AppType: XpressAppTypeEnum;
    PendingRequestId: string | null;
    PendingRequestedAt: string | null;
    PendingRequestedBy: string | null;
    LastAckedAt: string | null;
}

@Component({
    selector: 'send-logs-request',
    templateUrl: './send-logs-request.component.html',
    providers: [MessageService]
})
export class SendLogsRequestComponent implements OnInit, OnChanges {
    @Input() public userId!: number;
    @Input() public forDriver = false;

    public readonly everyDayApp = XpressAppTypeEnum.EveryDayApp;
    public appOptions = [
        { label: 'DriverApp', value: XpressAppTypeEnum.DriverApp },
        { label: 'BYD Taxi', value: XpressAppTypeEnum.BYDTaxiApp }
    ];

    public selectedAppTypes: XpressAppTypeEnum[] = [];
    public statuses: SendLogRequestStatus[] = [];
    public canRequest: boolean = false;
    public loading: boolean = false;

    constructor(
        private messageService: MessageService,
        private xpressUserService: XpressUserService,
        private riderService: RiderService,
        private appDataService: AppDataService
    ) { }

    ngOnInit(): void {
        this.canRequest = this.hasRequestPermission();
        this.loadStatuses();
    }

    // The host panel is reused for another customer without being recreated, so the statuses of the
    // previous one must not stay on screen.
    ngOnChanges(changes: SimpleChanges): void {
        const userIdChange = changes['userId'];

        if (!userIdChange || userIdChange.isFirstChange()
            || userIdChange.currentValue === userIdChange.previousValue) {
            return;
        }

        this.statuses = [];
        this.selectedAppTypes = [];
        this.loadStatuses();
    }

    public loadStatuses(): void {
        if (!this.userId || !this.hasViewPermission()) { return; }

        const requestedUserId = this.userId;
        const dto = { XpressUserId: requestedUserId };

        this.sendLogApi.getSendLogRequests(dto, this.messageService)
            .then(data => {
                // A slower response for a previously selected customer must not overwrite the current one.
                if (requestedUserId !== this.userId || !data.Success) { return; }

                this.statuses = data.Statuses || [];
            })
            .catch(() => {
                if (requestedUserId === this.userId) {
                    this.statuses = [];
                }
            });
    }

    public requestLogs(): void {
        const appTypes = this.forDriver
            ? this.selectedAppTypes
            : [XpressAppTypeEnum.EveryDayApp];

        if (!appTypes.length || this.loading) { return; }

        this.loading = true;

        const dto = {
            XpressUserId: this.userId,
            AppTypes: appTypes
        };

        this.sendLogApi.requestSendLogs(dto, this.messageService)
            .then(data => {
                this.loading = false;

                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.messageService.add({
                    severity: 'success',
                    summary: 'Logs requested',
                    detail: 'The apps will upload their logs on the next profile refresh'
                });

                this.selectedAppTypes = [];
                this.loadStatuses();
            })
            .catch(() => this.loading = false);
    }

    public getStatusText(appType: XpressAppTypeEnum): string {
        const status = this.statuses.find(x => x.AppType === appType);

        if (!status) { return 'never requested'; }

        if (status.PendingRequestedAt) {
            const requestedBy = status.PendingRequestedBy ? ` by ${status.PendingRequestedBy}` : '';

            return `pending since ${this.formatDate(status.PendingRequestedAt)}${requestedBy}`;
        }

        if (status.LastAckedAt) {
            return `last ack ${this.formatDate(status.LastAckedAt)}`;
        }

        return 'never requested';
    }

    private formatDate(value: string): string {
        return moment.utc(value).local().format('DD MMM YYYY HH:mm');
    }

    private get sendLogApi(): XpressUserService | RiderService {
        return this.forDriver ? this.riderService : this.xpressUserService;
    }

    private hasViewPermission(): boolean {
        return this.appDataService.hasPermission(this.permissionMenu, MenuPermissionActionEnum.Read);
    }

    private hasRequestPermission(): boolean {
        return this.appDataService.hasPermission(this.permissionMenu, MenuPermissionActionEnum.Update);
    }

    private get permissionMenu(): string {
        return this.forDriver
            ? MenuPermissionConfig.BACK_OFFICE_DRIVERS
            : MenuPermissionConfig.BACK_OFFICE_USERS;
    }
}
